/**
 * Typed fetch client. All API responses are {ok: true, data} | {ok: false, error}.
 * Throws ApiError on !ok so callers can show a toast.
 */

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(message: string, code: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

type Envelope<T> = { ok: true; data: T } | { ok: false; error: { code: string; message: string } };

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const hasBody = init.body !== undefined && init.body !== null;
  const headers: Record<string, string> = { ...(init.headers as Record<string, string> | undefined) };
  if (hasBody && !Object.keys(headers).some((k) => k.toLowerCase() === 'content-type')) {
    headers['content-type'] = 'application/json';
  }
  const res = await fetch(path, { ...init, headers });
  const text = await res.text();
  let json: Envelope<T> | null = null;
  try { json = text ? (JSON.parse(text) as Envelope<T>) : null; } catch { /* not json */ }
  if (!json) {
    throw new ApiError(`Bad response from ${path}`, 'BAD_RESPONSE', res.status);
  }
  if (!json.ok) {
    throw new ApiError(json.error.message, json.error.code, res.status);
  }
  return json.data;
}

export const api = {
  get: <T>(path: string) => call<T>(path),
  post: <T>(path: string, body?: unknown) => call<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) => call<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => call<T>(path, { method: 'DELETE' }),
};

// ── Typed route helpers ─────────────────────────────────────────────
export type AuthStatus = {
  connected: boolean;
  shop_id: string | null;
  shop_name: string | null;
  expires_at: number | null;
  has_api_key: boolean;
};

export type Listing = {
  id: number;
  etsy_listing_id: number | null;
  state: string | null;
  title: string | null;
  description: string | null;
  price_amount: number | null;
  price_currency: string | null;
  quantity: number | null;
  tags: string | null;
  status: string;
  updated_at: number;
};

export type Draft = {
  id: number;
  brief: string;
  status: string;
  image_variants: Array<{ id: string; url: string; prompt: string; seed: string }>;
  copy_variants: Array<{ id: string; title?: string; description?: string; tags?: string }>;
  tag_variants: string[][];
  taxonomy_id: number | null;
  created_at: number;
  updated_at: number;
};

export type AnalyticsOverview = {
  listings_total: number;
  listings_active: number;
  orders_total: number;
  revenue_total: number;
  views_30d: number;
  favorites_30d: number;
};

export type StudioSession = {
  authenticated: boolean;
  email: string | null;
};

export const StudioAuth = {
  session: () => api.get<StudioSession>('/api/studio-auth/session'),
  login: (email: string, password: string) => api.post<{ email: string }>('/api/studio-auth/login', { email, password }),
  logout: () => api.post<{ logged_out: boolean }>('/api/studio-auth/logout'),
};

export const Auth = {
  start: () => api.post<{ url: string; state: string }>('/api/auth/start'),
  status: () => api.get<AuthStatus>('/api/auth/status'),
  disconnect: () => api.post<{ disconnected: boolean }>('/api/auth/disconnect'),
};

export const Listings = {
  list: (params?: { status?: string }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    const s = q.toString();
    return api.get<Listing[]>(`/api/listings${s ? `?${s}` : ''}`);
  },
  get: (id: string | number) => api.get<Listing>(`/api/listings/${id}`),
  create: (body: Record<string, unknown>) => api.post<{ id: number; persisted: string }>('/api/listings', body),
  update: (id: number, body: Record<string, unknown>) => api.put<unknown>(`/api/listings/${id}`, body),
  remove: (id: number) => api.del<{ deleted: boolean }>(`/api/listings/${id}`),
  sync: () => api.post<{ synced: number }>('/api/listings/sync'),
  uploadImage: (id: number, body: { data_base64: string; filename?: string; rank?: number; alt_text?: string }) =>
    api.post<{ uploaded: { listing_image_id: number; url_75x75: string; url_170x135: string; url_570xN: string; url_fullxfull: string } }>(`/api/listings/${id}/images`, body),
  reorderImages: (id: number, image_ids: number[]) =>
    api.put<{ reordered: number }>(`/api/listings/${id}/images/order`, { image_ids }),
  deleteImage: (id: number, imageId: number) =>
    api.del<{ deleted: number }>(`/api/listings/${id}/images/${imageId}`),
};

export const Drafts = {
  create: (body: { brief: string; n_images?: number; n_copy?: number }) => api.post<Draft>('/api/drafts', body),
  list: () => api.get<Draft[]>('/api/drafts'),
  get: (id: number) => api.get<Draft>(`/api/drafts/${id}`),
  remove: (id: number) => api.del<{ deleted: boolean }>(`/api/drafts/${id}`),
};

export const Images = {
  generate: (body: { prompt: string; n?: number; aspect_ratio?: string }) =>
    api.post<Array<{ id: string; url: string; prompt: string; seed: string }>>('/api/images/generate', body),
  list: () => api.get<Array<{ id: number; url_570: string; prompt: string; alt_text: string }>>('/api/images'),
};

export const Content = {
  copy: (body: { brief: string; kind: 'title' | 'description' | 'tags'; n?: number }) =>
    api.post<Array<{ id: string; title?: string; description?: string; tags?: string }>>('/api/content/copy', body),
  description: (body: { brief: string; n?: number }) =>
    api.post<Array<{ id: string; description: string }>>('/api/content/description', body),
  tags: (body: { brief: string; n?: number }) =>
    api.post<Array<{ id: string; tags: string }>>('/api/content/tags', body),
  trending: (seed: string) => api.post<Array<{ tag: string; rank: number }>>('/api/content/trending-tags', { seed }),
};

export const Analytics = {
  overview: () => api.get<AnalyticsOverview>('/api/analytics/overview'),
  listings: () => api.get<Array<{ id: number; title: string; status: string; views: number; favorites: number; sales: number; revenue: number }>>('/api/analytics/listings'),
  orders: () => api.get<Array<{ date: string; orders: number; revenue: number }>>('/api/analytics/orders'),
};

export type Order = {
  id: number;
  etsy_order_id: string;
  buyer_email: string;
  name: string;
  total_amount: number;
  currency: string;
  status: string;
  created_at: number;
  raw: string | null;
};

export const Orders = {
  list: () => api.get<Array<Omit<Order, 'raw'>>>('/api/orders'),
  get: (id: string | number) => api.get<Order>(`/api/orders/${id}`),
};

export const Settings = {
  get: () => api.get<{ settings: Record<string, { value: unknown; updated_at: number }>; env: Record<string, unknown> }>('/api/settings'),
  put: (updates: Array<{ key: string; value: unknown }>) => api.put<{ updated: number }>('/api/settings', { updates }),
};
